import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Package, Check, ChevronsUpDown } from "lucide-react";

/**
 * ProductPicker — small combobox that lets a user pick a product from the
 * catalog and auto-fills the parent line item (description, price, GST flag,
 * service-fee flag). Falls back to a plain "Search catalog" button when there
 * are no products, so users can still add manual line items.
 *
 * Props:
 *  - onPick(product) — called with the full product record on selection
 *  - selectedId? — currently selected product id (for the checkmark)
 *  - disabled?
 *  - buttonSize? — "icon" | "sm" (default "icon")
 */
export default function ProductPicker({ onPick, selectedId, disabled, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data: products = [] } = useQuery({
    queryKey: ["products-picker"],
    queryFn: () => api.get("/products").then((r) => r.data),
    staleTime: 30_000,
  });
  const filtered = useMemo(() => {
    if (!q) return products;
    const needle = q.toLowerCase();
    return products.filter((p) =>
      (p.name || "").toLowerCase().includes(needle) ||
      (p.description || "").toLowerCase().includes(needle) ||
      (p.category || "").toLowerCase().includes(needle),
    );
  }, [products, q]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          disabled={disabled}
          className="rounded-sm h-9 w-9"
          title="Pick from catalog"
          data-testid={testId || "line-product-picker"}
        >
          <Package className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[320px]" align="start" data-testid="product-picker-popover">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search products…" value={q} onValueChange={setQ} />
          <CommandList>
            <CommandEmpty>
              <div className="text-xs text-muted-foreground p-2">
                {products.length === 0
                  ? "No products in your catalog yet."
                  : "No matches."}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.id}
                  onSelect={() => { onPick(p); setOpen(false); setQ(""); }}
                  data-testid={`product-pick-${p.id}`}
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1">
                        {selectedId === p.id && <Check className="h-3 w-3 text-primary" />}
                        {p.name}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {p.category ? `${p.category} · ` : ""}{p.description || ""}
                      </div>
                    </div>
                    <div className="text-xs font-semibold whitespace-nowrap">
                      SGD {(p.unit_price || 0).toFixed(2)}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
